/**
 * Stock Planning's Export menu and Print button.
 *
 * Split out of StockPlanningTab for the 500-line cap (CR-8193). The menu keeps
 * CR-7163 (c)'s two labelled groups: this grid's columns, and the supplier's
 * warehouse stock — the second needs one supplier, so it is off while the
 * grid shows every call-off supplier.
 */
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, Loader2, Printer } from 'lucide-react';

interface StockPlanningExportMenuProps {
  /** A supplier, or every call-off supplier, is chosen. */
  hasSupplier: boolean;
  /** The grid shows every call-off supplier. */
  allSuppliers: boolean;
  exporting: boolean;
  printing: boolean;
  onExport: (fileFormat: 'csv' | 'xlsx') => void;
  onExportSupplierStock: (fileFormat: 'csv' | 'xlsx') => void;
  onPrint: () => void;
}

export default function StockPlanningExportMenu({
  hasSupplier, allSuppliers, exporting, printing, onExport, onExportSupplierStock, onPrint,
}: StockPlanningExportMenuProps) {
  const busy = exporting || printing;
  const supplierStockDisabled = !hasSupplier || allSuppliers || busy;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={!hasSupplier || busy}
        onClick={onPrint}
        title="Print every row the filters match, not just this page"
      >
        {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
        Print
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" disabled={!hasSupplier || busy}>
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export
          </Button>
        </DropdownMenuTrigger>
        {/* CR-7163 (c): two distinct reports, one control. Labelled
            groups rather than a second Export button, so "Export" doesn't
            appear twice meaning different things. */}
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Stock planning grid
          </DropdownMenuLabel>
          <DropdownMenuItem disabled={!hasSupplier || busy} onClick={() => onExport('xlsx')}>
            <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
            Download Excel
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!hasSupplier || busy} onClick={() => onExport('csv')}>
            <FileText className="h-4 w-4 mr-2 text-blue-700" />
            Download CSV
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Supplier warehouse stock
            {allSuppliers && (
              <span className="block font-normal">Pick one supplier to export its stock</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuItem disabled={supplierStockDisabled} onClick={() => onExportSupplierStock('xlsx')}>
            <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
            Download Excel
          </DropdownMenuItem>
          <DropdownMenuItem disabled={supplierStockDisabled} onClick={() => onExportSupplierStock('csv')}>
            <FileText className="h-4 w-4 mr-2 text-blue-700" />
            Download CSV
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
